import roomAPIService from "../../../roomAPIService";
import store from "../../../../../store";
import { connect, createLocalTracks } from "twilio-video";
import { handleLocalParticipant, setFirstConnectFalse } from "./localParticipant";
import { handleExistingRemoteParticipants, handleFutureRemoteConnections, setFirstRemoteConnectFalse } from "./remoteParticipants";
import { handleDisconnections, handleReconnections } from "./connecting";
import { getSessionPreferences } from "../session";
import { getMediaOptions } from "../devices";
import { setDefaultLayout } from "../layout";
import { setChatDefaults } from "../chat";
import { isAnyAudioTrackEnabled, isAnyVideoTrackEnabled, stopAndUnpublishVideo, stopAndUnpublishAudio, toggleAudio, toggleVideo } from "../media";
import { registerJoinTimestamp } from "../timestamps";
import sessionSocket from "../../../../../socket/sessionSocket";

const AuthStore = store.state.auth; // !!! AuthStore.user IS NOT AVAILABLE HERE, THAT IS WHY WE GET AUTHSTORE ONLY

/*
before a user enters a new room, disconnect the user
from they joined already leaveRoomIfJoined();
remove any remote track when joining a new room
*/

async function getAccessToken() {
  return await roomAPIService.getTwilioAccessToken(AuthStore.user._id);
}

export async function attemptLaunchVideo(sessionID, fromReconnect = false) {
  let initialCameraState = fromReconnect ? store.state.mediastates.userMediaSettings.cameraOn : null;
  let initialMicState = fromReconnect ? store.state.mediastates.userMediaSettings.microphoneOn : null;

  try {
    /* Get token to authorize access to Room */
    const data = await getAccessToken();

    /* Reset any audio-video tracks if exist */
    await resetAudioVideoTracks();

    if (store.state.room.room) {
      store.state.room.room.disconnect();
    }
    /* Clear and reset room */
    await store.dispatch("room/resetRoom");

    // /* Initialize loading */
    if (fromReconnect === false) {
      store.dispatch("roomLoading/setLoadingState", true);
    }

    /* Confirm token existence */
    if (!data || !data.token) {
      throw new Error("Failed to get Access Token data.");
    }

    /* Get and set session preferences */
    await getSessionPreferences();

    /* Set default layout */
    setDefaultLayout();

    /* Set chat defaults */
    setChatDefaults();

    /* Get media options based on preferences and device availability for mic and cam */
    let { audioOptions, videoOptions } = await getMediaOptions();

    /* Based on preferences, get tracks from desired tracks */
    const tracks = await createLocalTracks({ audio: audioOptions, video: videoOptions });

    /* Prepare options to connect to Room with */
    let connectOptions = {
      name: sessionID,
      // logLevel: 'debug',
      tracks: tracks,
      enableDscp: true,
    };

    /* Max Video Bitrate: Default unlimited auto */
    const sessionPreferences = store.state.room.sessionPreferences;
    if (sessionPreferences && sessionPreferences.maxVideoBitrate) {
      connectOptions.maxVideoBitrate = sessionPreferences.maxVideoBitrate;
    }

    /* Attempt connect to Room */
    await connectToRoom(data.token, connectOptions, sessionID, fromReconnect, initialCameraState, initialMicState);
  } catch (error) {
    await handleMediaError(error);
  }
}

/**
 * Join a Room.
 * @param token - the AccessToken used to join a Room
 * @param connectOptions - the ConnectOptions used to join a Room
 */
async function connectToRoom(token, connectOptions, sessionID, fromReconnect, initialCameraState = null, initialMicState = null) {
  try {
    /* Join to the Room with the given AccessToken and ConnectOptions. */
    const room = await connect(token, connectOptions);
    const roomDetails = { room, sessionID };
    await store.dispatch("room/setRoomDetails", roomDetails);

    /***************************************
     * GOAL 1: HANDLE LOCAL USER (SELF)
     ***************************************/
    await handleLocalParticipant(room, sessionID);

    /***************************************
     * GOAL 2: HANDLE USERS ALREADY IN THE ROOM (IF ANY)
     ***************************************/
    handleExistingRemoteParticipants(room);

    /***************************************
     * GOAL 3: HANDLE FUTURE REMOTE CONNECTIONS TO THE ROOM
     ***************************************/
    handleFutureRemoteConnections(room);

    /***************************************
     * GOAL 4: HANDLE RECONNECTING TO THE ROOM
     ***************************************/
    handleReconnections(room);

    /***************************************
     * GOAL 5: HANDLE DISCONNECTIONS FROM THE ROOM
     ***************************************/
    handleDisconnections(room);

    /* Set successful connected status */
    if (fromReconnect) {
      await store.dispatch("sessionState/setStatusMessage", "connected");
      if (typeof initialCameraState === "boolean" && typeof initialMicState === "boolean") {
        await store.dispatch("mediastates/setReconnectMediaDevices", { camera: initialCameraState, microphone: initialMicState });
      }
      if (typeof initialCameraState === "boolean" && isAnyVideoTrackEnabled() !== initialCameraState) {
        await toggleVideo();
      }
      if (typeof initialMicState === "boolean" && isAnyAudioTrackEnabled() !== initialMicState) {
        await toggleAudio();
      }
    } else {
      /* Set initial media states */
      await store.dispatch("sessionState/setInitialState");
    }

    /* Record participant's successful complete connection to the Cofocus session */
    await registerJoinTimestamp();

    /* ====== ENSURE PARTNER STATE IS ACCURATE AT START ====== */
    /** Note that socket MUST be initialized BEFORE this is called. */
    await sessionSocket.requestPartnerState();
  } catch (error) {
    await handleMediaError(error);
  }
}

/* Twilio media errors */
async function handleMediaError(error) {
  // https://www.twilio.com/docs/video/build-js-video-application-recommendations-and-best-practices#handling-page-unload
  // const mediaErrors = ["NotAllowedError", "NotFoundError", "NotReadableError", "OverconstrainedError", "TypeError"];
  console.error("Failed to acquire media:", error.name, error.message);

  await roomAPIService.logSessionError(error);

  // Depending on case, show user a different message which they can close
  if (error && error.name) {
    store.dispatch("sessionState/setStatusMessage", error.name);
  }
}

/* Complete reset, ONLY to be executed during
exiting the session or changing session,
as this will reset everything and therefore
disable all functionality. */
export async function resetRoom() {
  /* Reset audio video tracks */
  await resetAudioVideoTracks();

  const room = store.state.room.room;

  /* Disconnect from the Room, must do this 
  explicitly, restarting twilio is not enough
  or user will be in previous room and cannot
  connect to new room. */
  if (room) {
    room.disconnect();
  }

  setFirstConnectFalse();
  setFirstRemoteConnectFalse();

  /* CLEAR ALL ROOM & SESSION VUEX */

  /* Reset toolbar state Vuex data */
  store.dispatch("toolbar/resetState");

  /* Reset Room Vuex data */
  store.dispatch("room/resetRoom");

  /* Reset session Vuex data */
  store.dispatch("session/resetSessionStates");

  /* Reset remote profile data */
  store.dispatch("remoteProfileData/reset");

  /* Reset session media states Vuex data */
  store.dispatch("mediastates/resetMediaStates");

  /* Reset session preferences Vuex data */
  store.dispatch("preferences/resetSessionPreferences");
}

async function resetAudioVideoTracks() {
  /* Stop the camera */
  if (isAnyVideoTrackEnabled()) {
    await stopAndUnpublishVideo();
  }

  /** Stop the microphone */
  if (isAnyAudioTrackEnabled()) {
    await stopAndUnpublishAudio();
  }
}

export default {
  attemptLaunchVideo,
  resetRoom,
};
